import { Link } from "@/lib/router-compat";
import { ArrowRight, MapPin, Building2, Hotel, Store, UtensilsCrossed } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import MagneticButton from "@/components/MagneticButton";
import Testimonials from "@/components/Testimonials";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const secteurs = [
  { icon: UtensilsCrossed, title: "Restaurants & bars", desc: "Nombre de couverts, largeur de passage en service, acoustique, place réelle du bar et du passe. La salle doit être belle le soir et tenable un samedi midi." },
  { icon: Hotel, title: "Hôtels & boutique-hôtels", desc: "Chambres, accueil, espaces communs. Des matériaux qui supportent le ménage quotidien et un parcours client lisible dès la porte d'entrée." },
  { icon: Store, title: "Commerces & bureaux", desc: "Vitrine, chemin de circulation, zones de travail, accessibilité PMR. On dessine à partir de vos contraintes de bail et de vos horaires d'ouverture." },
];


interface FeaturedProject {
  id: string; title: string; slug: string; cover_image: string | null; location: string | null;
}

const AmenagementProfessionnel = () => {
  const [projects, setProjects] = useState<FeaturedProject[]>([]);

  const featuredSlugs = ["projet-mineral", "projet-aubergine", "projet-chablis"];

  useEffect(() => {
    supabase.from("projects").select("id, title, slug, cover_image, location")
      .eq("published", true)
      .in("slug", featuredSlugs)
      .then(({ data }) => {
        const ordered = featuredSlugs
          .map(s => (data ?? []).find(p => p.slug === s))
          .filter(Boolean) as FeaturedProject[];
        setProjects(ordered);
      });
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Aménagement professionnel & hôtellerie Lyon | JIIA Studio"
        description="Architecte d'intérieur spécialisée en aménagement professionnel à Lyon : hôtels, restaurants, bureaux, commerces. Conception, images 3D et suivi de chantier."
        canonical="/amenagement-professionnel-hotellerie"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Aménagement professionnel & hôtellerie Lyon",
            "provider": { "@type": "LocalBusiness", "name": "JIIA Studio", "url": "https://jiia-studio.com" },
            "areaServed": [{ "@type": "City", "name": "Lyon" }, { "@type": "AdministrativeArea", "name": "Rhône-Alpes" }],
            "serviceType": "Aménagement professionnel",
            "description": "Architecture d'intérieur pour hôtels, restaurants, bureaux et commerces en Rhône-Alpes."
          }
        ]}
      />

      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <div className="flex items-center gap-2 text-sm text-muted-foreground font-body mb-4">
              <Building2 className="h-4 w-4" />
              <span>Professionnels · Hôtellerie · Restauration · Commerces</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-light leading-tight mb-6">
              Aménagement professionnel<br />
              <span className="italic">à Lyon et en Rhône-Alpes</span>
            </h1>
            <p className="text-lg text-muted-foreground font-body max-w-2xl leading-relaxed mb-4">
              Un local commercial se juge en dix secondes depuis le trottoir, puis se vit pendant des années par vos équipes.
            </p>
            <p className="text-muted-foreground font-body max-w-2xl leading-relaxed mb-4">
              Je dessine des restaurants, des hôtels, des cabinets et des boutiques à Lyon et en Rhône-Alpes. Le travail commence rarement par les couleurs : d'abord les flux, le nombre de places, les normes d'accessibilité et de sécurité, le budget d'exploitation. L'ambiance vient ensuite et elle tient mieux quand le plan est juste.
            </p>
            <p className="text-muted-foreground font-body max-w-2xl leading-relaxed mb-8">
              Je travaille surtout avec des indépendants et des petites structures, qui financent leur projet eux-mêmes et n'ont pas droit à l'erreur sur un poste de travaux.
            </p>
            <p className="text-foreground font-body font-medium max-w-2xl mb-8">
              Premier rendez-vous offert.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-sm font-body text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors">
                    Discuter de votre projet <ArrowRight className="h-4 w-4" />
                  </span>
                </MagneticButton>
              </Link>
              <Link to="/projets?filtre=Professionnel">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 border border-border rounded-sm font-body text-sm uppercase tracking-widest hover:bg-secondary transition-colors">
                    Voir les réalisations pro
                  </span>
                </MagneticButton>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Secteurs */}
      <section className="py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Secteurs</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
              Les lieux sur lesquels j'interviens
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {secteurs.map((s, i) => (
              <AnimatedSection key={s.title} delay={i * 0.1}>
                <div className="p-6 border border-border rounded-sm hover:border-accent/50 transition-colors h-full">
                  <s.icon className="h-6 w-6 text-accent mb-3" />
                  <h3 className="font-heading text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{s.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>


      {/* Projets */}
      {projects.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="container">
            <AnimatedSection>
              <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Portfolio</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
                Projets professionnels & hôtellerie
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {projects.map((project, i) => (
                <AnimatedSection key={project.id} delay={i * 0.1}>
                  <Link to={`/projets/${project.slug}`} className="group block">
                    <div className="aspect-[16/10] overflow-hidden rounded-sm mb-3">
                      <img src={project.cover_image || ""} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    </div>
                    <h3 className="font-heading text-lg group-hover:text-accent transition-colors">{project.title}</h3>
                    {project.location && <p className="text-xs text-muted-foreground font-body flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {project.location}</p>}
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-4">
              Vous avez un local, une date d'ouverture ?
            </h2>
            <p className="text-primary-foreground/70 font-body max-w-lg mx-auto mb-8 leading-relaxed">
              Envoyez-moi la surface, le bail et votre échéance. Premier rendez-vous offert, réponse sous 48h.
            </p>
            <Link to="/contact">
              <MagneticButton>
                <span className="inline-flex items-center gap-2 px-10 py-4 bg-rose text-white rounded-sm font-body text-sm uppercase tracking-widest hover:bg-rose/90 transition-colors">
                  Prendre rendez-vous <ArrowRight className="h-4 w-4" />
                </span>
              </MagneticButton>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default AmenagementProfessionnel;
